import {
  Component,
  ContentChild,
  Input,
  OnChanges,
  SecurityContext,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { GridColumnGroupComponent } from '../grid-column-group/grid-column-group.component';
import { GridColumnHeaderComponent } from '../grid-column-header/grid-column-header.component';
import { GridRowNode, isGridRowGroup } from '../grid-table/grid-table.component';

export interface GridColumnCellContext<T extends {}, V> {
  $implicit?: V;
  data?: T;
  row?: GridRowNode<T>;
}

type AggregateMethod<T> = (a: T, b: T) => T;

type ColumnInput<T> =
  | {
      field: keyof T;
      getValue: undefined;
    }
  | {
      field: string;
      getValue: (row: T) => any;
    };

type ColumnValue<T, Input extends ColumnInput<T>> = Input extends {
  getValue: (row: T) => infer V;
}
  ? V extends {}
    ? V
    : never
  : Input extends {
        field: infer K;
      }
    ? K extends keyof T
      ? T[K]
      : never
    : never;

@Component({
  selector: 'app-grid-column',
  template: '',
  standalone: false,
})
export class GridColumnComponent<T extends {}, Input extends ColumnInput<T>> implements OnChanges {
  @Input() headerName: string;
  @Input() headerAbbreviation?: string | null;
  @Input() headerTooltip: string;
  @Input() field: Input['field'];
  @Input() getValue?: Input['getValue'];
  @Input() fixed?: 'left' | 'right';
  @Input() cellClass?: string | string[];
  @Input() columnGroupShow?: 'open' | 'closed' | 'both';
  @Input() rowGroup = false;
  @Input() hide = false;

  @Input() width?: number;
  @Input() minWidth?: number;
  @Input() maxWidth?: number;
  @Input() grow?: number;

  @Input() aggFunc:
    | 'sum'
    | 'first'
    | 'last'
    | AggregateMethod<ColumnValue<T, Input>>
    | null
    | undefined;

  @ContentChild(TemplateRef, { descendants: false }) cellTemplate: TemplateRef<
    GridColumnCellContext<T, ColumnValue<T, Input>>
  >;
  @ContentChild(GridColumnHeaderComponent) header?: GridColumnHeaderComponent;

  valueGetter: (row: T) => ColumnValue<T, Input> | null = _ => null;

  // Set in GridColumnGroupComponent
  group: GridColumnGroupComponent<T> | null = null;

  // Set in GridTableComponent
  isHeader = false;

  // Set in ValueFormatDirective
  valueFormatter?: (value: ColumnValue<T, Input>) => any;

  headerHtml: string | null = null;
  get headerTemplate() {
    return this.header?.template;
  }

  get hasAggregate() {
    return !!this._aggFunc;
  }

  get hidden() {
    if (this.hide) {
      return true;
    }
    if (!this.group) {
      return false;
    }
    return this.group.open ? this.columnGroupShow === 'closed' : this.columnGroupShow === 'open';
  }

  private _aggFunc?: AggregateMethod<ColumnValue<T, Input>>;

  constructor(private sanitizer?: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges) {
    if ('aggFunc' in changes) {
      if (typeof this.aggFunc === 'function') {
        this._aggFunc = this.aggFunc;
      } else if (this.aggFunc === 'sum') {
        this._aggFunc = (a, b) => (a == null ? b : b == null ? a : (a as any) + (b as any));
      } else if (this.aggFunc === 'first') {
        this._aggFunc = a => a;
      } else if (this.aggFunc === 'last') {
        this._aggFunc = (_a, b) => b;
      } else {
        this._aggFunc = undefined;
      }
    }
    if ('field' in changes || 'getValue' in changes) {
      if (this.getValue) {
        this.valueGetter = this.getValue;
      } else if (this.field) {
        this.valueGetter = row => row?.[this.field as any] ?? null;
      } else {
        this.valueGetter = _ => null;
      }
    }
    if ('headerName' in changes || 'headerAbbreviation' in changes) {
      this.buildHeaderHtml();
    }
  }

  private buildHeaderHtml() {
    if (!this.headerAbbreviation || !this.sanitizer) {
      this.headerHtml = null;
      return;
    }

    const nameParts = this.headerName.split(/ +/g);
    const abbrParts = this.headerAbbreviation.split(/ +/g);

    let currentAbbr: string[] = [];
    let currentAbbrTitle: string[] = [];
    const parts: string[] = [];
    for (let i = 0; i <= abbrParts.length; i++) {
      const abbr = abbrParts[i];
      while (abbr == null || nameParts.indexOf(abbr) > 0) {
        const title = nameParts.shift();
        if (title == null) {
          break;
        }
        currentAbbrTitle.push(title);
      }

      if (abbr == null || abbr === nameParts[0]) {
        if (currentAbbr.length && currentAbbrTitle.length) {
          parts.push(`<abbr title="${currentAbbrTitle.join(' ')}">${currentAbbr.join(' ')}</abbr>`);
          currentAbbr = [];
          currentAbbrTitle = [];
        }
        if (abbr) {
          parts.push(abbr);
          nameParts.shift();
        }
      } else {
        currentAbbr.push(abbr);
      }
    }

    this.headerHtml = this.sanitizer.sanitize(SecurityContext.HTML, parts.join(' '));
  }

  aggregate(rows: ReadonlyArray<GridRowNode<T>>): ColumnValue<T, Input> | null {
    if (!this._aggFunc) {
      return null;
    }

    const values: Array<ColumnValue<T, Input>> = [];
    const addValues = (row: GridRowNode<T>) => {
      let value;
      if (isGridRowGroup(row)) {
        if (!row.groupData) {
          row.children?.forEach(addValues);
          return;
        }
        value = this.valueGetter(row.groupData);
      } else if (row.state === 'LOADED') {
        value = this.valueGetter(row.data);
      }
      if (value != null && (value as any) !== '') {
        values.push(value);
      }
    };

    rows?.forEach(addValues);

    if (values.length === 0) {
      return null;
    }

    return values.reduce(this._aggFunc);
  }
}
